import React, {
  useEffect,
  useCallback,
  useState,
  useContext,
  useRef,
  useMemo,
} from "react";
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View as RNView,
  Modal,
  Pressable,
  ActivityIndicator,
  TextInput,
  Image,
  FlatList,
  RefreshControl,
  Platform,
  useWindowDimensions,
} from "react-native";
import FeatherIcon from "react-native-vector-icons/Feather";
import { useDispatch, useSelector } from "react-redux";
import { useAppTheme } from "../../../../hooks/useAppTheme";
import { ThemeColors } from "../../../../utils/constants/colors";
import DeviceOrientationContext from "../../../../components/context/DeviceOrientationContext";
import { View } from "../../../../components/shared/Themed";
import {
  getOrganizations,
  listAllPortalStates,
  updateOrganization,
  getSalesCustomers,
  createSalesOrder,
} from "../../../../redux/dispatcher/portalDispatcher";
import {
  setOrganizationPage,
  setOrganizationSortBy,
  setOrganizationSearchQuery,
} from "../../../../redux/reducers/portalReducer";
import AddEditOrganizationModal, { OrgSavePayload } from "./modal";
import CreateSalesOrderModal, { SalesOrderPayload } from "../CreateSalesOder";
import { useSalesOrderLoaders } from "../utils/SalesOrderLoader";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const getC = (colors: ThemeColors, isDark: boolean) => ({
  navy: isDark ? colors.surface : "#0F1B2D",
  navyMid: isDark ? colors.surfaceSubtle : "#1A2E46",
  navyLight: isDark ? colors.borderSubtle : "#243B55",
  accent: colors.primary,
  success: colors.success,
  successBg: isDark ? "rgba(14, 159, 110, 0.2)" : "#EDFAF4",
  warning: colors.warning,
  warningBg: isDark ? "rgba(245, 158, 11, 0.2)" : "#FFF8EC",
  danger: colors.danger,
  dangerBg: isDark ? "rgba(231, 76, 60, 0.2)" : "#FEF0EE",
  surface: colors.surface,
  surfaceAlt: isDark ? colors.surfaceSubtle : "#F5F7FB",
  border: colors.borderSubtle,
  borderLight: colors.borderSubtle,
  text: colors.text,
  textMid: colors.textSecondary,
  textMuted: colors.textMuted,
  overlay: "rgba(10,20,35,0.5)",
});

type Tokens = ReturnType<typeof getC>;

const useTokens = () => {
  const { colors, isDark } = useAppTheme();
  return useMemo(() => getC(colors, isDark), [colors, isDark]);
};

const WIDE_BREAKPOINT = 768;
const webNoOutline =
  Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : null;

// ─── Types ────────────────────────────────────────────────────────────────────
/** The signed-in user's role, from `state.configurations.currentRole`. */
type UserRole = "OWNER" | "ADMIN" | "MEMBER";

interface Organization {
  _id: string;
  name: string;
  description?: string;
  cardCode?: string | string[];
  prefix?: string;
  logo?: string;
  logoUrl?: string;
  domains?: string[];
  states?: string[];
  contacts?: { address: string; email: string; phoneNumber: string }[];
  sales?: { name: string; email: string }[];
  pricingEnabled?: boolean;
  pricingModel?: string;
  pricingSalesEmail?: string | null;
  showAvailableStock?: boolean;
  showOnOrderStock?: boolean;
  showStockOnTransit?: boolean;
  showGrowers?: boolean;
  createdAt?: string;
}

interface OrgAccess {
  /** Assigned sales rep for this org — the only one who can create orders. */
  isRep: boolean;
  /** OWNER or ADMIN — can open and manage every org. */
  isAdmin: boolean;
  role: UserRole | null;
  canAccess: boolean;
}

type ViewMode = "grid" | "list";
type SortOption = "name-asc" | "name-desc" | "created-desc" | "created-asc";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SORT_OPTIONS: { value: SortOption; label: string; icon: string }[] = [
  { value: "name-asc", label: "Name A–Z", icon: "arrow-down" },
  { value: "name-desc", label: "Name Z–A", icon: "arrow-up" },
  { value: "created-desc", label: "Newest first", icon: "clock" },
  { value: "created-asc", label: "Oldest first", icon: "clock" },
];

const ADMIN_ROLES = new Set<UserRole>(["OWNER", "ADMIN"]);

const roleLabel = (role: UserRole | null) =>
  role === "OWNER" ? "Owner" : role === "ADMIN" ? "Admin" : "Member";

const getInitials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

const cardCodes = (org: Organization): string[] =>
  ([] as string[]).concat(org.cardCode ?? []).filter(Boolean);

const codesSummary = (org: Organization) => {
  const codes = cardCodes(org);
  if (!codes.length) return "No card code";
  return `${codes[0]}${codes.length > 1 ? ` +${codes.length - 1}` : ""}`;
};

const pricingLabel = (org: Organization) => {
  if (!org.pricingEnabled)
    return { label: "Pricing off", tone: "muted" as const };
  if (org.pricingModel === "sales")
    return { label: "Sales pricing", tone: "accent" as const };
  return { label: "Default pricing", tone: "neutral" as const };
};

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

// ─── Small pieces ─────────────────────────────────────────────────────────────

const Avatar = ({
  org,
  size,
  C,
}: {
  org: Organization;
  size: number;
  C: Tokens;
}) => (
  <RNView
    style={{
      width: size,
      height: size,
      borderRadius: size * 0.28,
      backgroundColor: C.accent + "18",
      justifyContent: "center",
      alignItems: "center",
      overflow: "hidden",
    }}
  >
    {org.logoUrl ? (
      <Image
        source={{ uri: org.logoUrl }}
        style={{ width: size, height: size }}
      />
    ) : (
      <Text
        style={{ fontSize: size * 0.36, fontWeight: "800", color: C.accent }}
      >
        {getInitials(org.name)}
      </Text>
    )}
  </RNView>
);

type PillTone = "accent" | "neutral" | "muted" | "success";

const Pill = ({
  label,
  icon,
  tone,
  C,
}: {
  label: string;
  icon?: string;
  tone: PillTone;
  C: Tokens;
}) => {
  const palette = {
    accent: { fg: C.accent, bg: C.accent + "14" },
    success: { fg: C.success, bg: C.successBg },
    neutral: { fg: C.textMid, bg: C.surfaceAlt },
    muted: { fg: C.textMuted, bg: C.surfaceAlt },
  }[tone];
  return (
    <RNView
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 999,
        backgroundColor: palette.bg,
        alignSelf: "flex-start",
      }}
    >
      {!!icon && <FeatherIcon name={icon} size={10} color={palette.fg} />}
      <Text
        style={{ fontSize: 11, fontWeight: "700", color: palette.fg }}
        numberOfLines={1}
      >
        {label}
      </Text>
    </RNView>
  );
};

const AccessPills = ({
  isRep,
  isAdmin = false,
  role = null,
  locked,
  compact,
  C,
}: {
  isRep: boolean;
  isAdmin?: boolean;
  role?: UserRole | null;
  locked: boolean;
  compact?: boolean;
  C: Tokens;
}) => (
  <>
    {isRep && (
      <Pill
        label={compact ? "Yours" : "Your customer"}
        icon="user-check"
        tone="success"
        C={C}
      />
    )}
    {isAdmin && (
      <Pill label={roleLabel(role)} icon="shield" tone="accent" C={C} />
    )}
    {locked && <Pill label="No access" icon="lock" tone="muted" C={C} />}
  </>
);

// ─── Responsive sheet: bottom sheet on phones, centred dialog on wide screens ─

const ResponsiveSheet: React.FC<{
  visible: boolean;
  onClose: () => void;
  maxWidth?: number;
  children: React.ReactNode;
}> = ({ visible, onClose, maxWidth = 460, children }) => {
  const C = useTokens();
  const RS = useMemo(() => createRS(C), [C]);
  const { width } = useWindowDimensions();
  const wide = width >= WIDE_BREAKPOINT;
  return (
    <Modal
      visible={visible}
      transparent
      animationType={wide ? "fade" : "slide"}
      onRequestClose={onClose}
    >
      <RNView
        style={[RS.backdrop, wide ? RS.backdropCenter : RS.backdropBottom]}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityLabel="Close"
        />
        <RNView
          style={[
            RS.panel,
            wide ? [RS.panelDialog, { maxWidth }] : RS.panelSheet,
          ]}
        >
          {!wide && <RNView style={RS.handle} />}
          {children}
        </RNView>
      </RNView>
    </Modal>
  );
};

const CloseButton = ({ onPress, C }: { onPress: () => void; C: Tokens }) => (
  <Pressable
    onPress={onPress}
    hitSlop={8}
    accessibilityLabel="Close"
    style={(s: any) => ({
      width: 32,
      height: 32,
      borderRadius: 8,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: s.hovered || s.pressed ? C.surfaceAlt : "transparent",
    })}
  >
    <FeatherIcon name="x" size={18} color={C.textMuted} />
  </Pressable>
);

// ─── Context Menu (organisation actions) ──────────────────────────────────────

interface ContextMenuProps {
  visible: boolean;
  org: Organization | null;
  access: OrgAccess;
  onClose: () => void;
  onCreateOrder: () => void;
  onEdit: () => void;
  onViewUsers: () => void;
  onViewOrders: () => void;
  onViewGrowers: () => void;
  onViewProducts: () => void;
  onViewDashboard: () => void;
  onAssignProducts: () => void;
}

interface MenuItem {
  label: string;
  description: string;
  icon: string;
  action: () => void;
  disabled?: boolean;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  visible,
  org,
  access,
  onClose,
  onCreateOrder,
  onEdit,
  onViewUsers,
  onViewOrders,
  onViewGrowers,
  onViewProducts,
  onViewDashboard,
  onAssignProducts,
}) => {
  const C = useTokens();
  const CM = useMemo(() => createCM(C), [C]);

  const items: MenuItem[] = [
    {
      label: "Orders",
      description: "Order history and status",
      icon: "file-text",
      action: onViewOrders,
      disabled: false,
    },
    {
      label: "Products",
      description: "Products available to this customer",
      icon: "package",
      action: onViewProducts,
      disabled: true,
    },
    {
      label: "Growers",
      description: "Growers linked to this organisation",
      icon: "grid",
      action: onViewGrowers,
      disabled: true,
    },
    {
      label: "Dashboard",
      description: "Activity and key numbers",
      icon: "bar-chart-2",
      action: onViewDashboard,
      disabled: true,
    },
    {
      label: "Assign products",
      description: "Choose what they can order",
      icon: "check-square",
      action: onAssignProducts,
      disabled: true,
    },
  ];

  const adminItems: MenuItem[] = access.isAdmin
    ? [
        {
          label: "Members",
          description: "People in this organisation",
          icon: "users",
          action: onViewUsers,
        },
        {
          label: "Edit organisation",
          description: "Details, pricing and visibility",
          icon: "edit-2",
          action: onEdit,
        },
      ]
    : [];

  const run = (fn: () => void) => {
    onClose();
    fn();
  };

  const renderItem = (item: MenuItem) => (
    <Pressable
      key={item.label}
      onPress={() => run(item.action)}
      accessibilityRole="button"
      style={(s: any) => [CM.row, (s.hovered || s.pressed) && CM.rowHover]}
      disabled={item.disabled}
    >
      <RNView style={CM.rowIcon}>
        <FeatherIcon
          name={item.icon}
          size={16}
          color={item.disabled ? C.textMuted : C.accent}
        />
      </RNView>
      <RNView style={{ flex: 1, minWidth: 0 }}>
        <Text style={CM.rowLabel} numberOfLines={1}>
          {item.label}
        </Text>
        <Text style={CM.rowDesc} numberOfLines={1}>
          {item.description}
        </Text>
      </RNView>
      <FeatherIcon name="chevron-right" size={16} color={C.textMuted} />
    </Pressable>
  );

  return (
    <ResponsiveSheet visible={visible} onClose={onClose} maxWidth={460}>
      {org && (
        <RNView style={CM.header}>
          <Avatar org={org} size={44} C={C} />
          <RNView style={{ flex: 1, minWidth: 0, gap: 4 }}>
            <Text style={CM.headerTitle} numberOfLines={1}>
              {org.name}
            </Text>
            <Text style={CM.headerSub} numberOfLines={1}>
              {cardCodes(org).join(" · ") || "No card code"}
            </Text>
            <RNView style={CM.headerPills}>
              <AccessPills
                isRep={access.isRep}
                isAdmin={access.isAdmin}
                role={access.role}
                locked={false}
                C={C}
              />
            </RNView>
          </RNView>
          <CloseButton onPress={onClose} C={C} />
        </RNView>
      )}

      {access.isRep && (
        <Pressable
          style={(s: any) => [
            CM.primaryBtn,
            (s.hovered || s.pressed) && { opacity: 0.9 },
          ]}
          onPress={() => run(onCreateOrder)}
          accessibilityRole="button"
        >
          <FeatherIcon name="shopping-cart" size={15} color="#fff" />
          <Text style={CM.primaryBtnText}>Create order</Text>
        </Pressable>
      )}

      <RNView style={CM.group}>{items.map(renderItem)}</RNView>

      {adminItems.length > 0 && (
        <>
          <Text style={CM.groupLabel}>Manage</Text>
          <RNView style={CM.group}>{adminItems.map(renderItem)}</RNView>
        </>
      )}
    </ResponsiveSheet>
  );
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

interface DeleteConfirmModalProps {
  visible: boolean;
  orgName?: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  visible,
  orgName,
  loading,
  onConfirm,
  onCancel,
}) => {
  const C = useTokens();
  const DC = useMemo(() => createDC(C), [C]);
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <RNView style={DC.backdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
        <RNView style={DC.card}>
          <RNView style={DC.iconWrap}>
            <FeatherIcon name="alert-triangle" size={24} color={C.danger} />
          </RNView>
          <Text style={DC.title}>Delete organisation?</Text>
          <Text style={DC.body}>
            <Text style={{ fontWeight: "800", color: C.text }}>{orgName}</Text>{" "}
            will be permanently deleted. This can't be undone.
          </Text>
          <RNView style={DC.actions}>
            <TouchableOpacity
              style={DC.cancelBtn}
              onPress={onCancel}
              disabled={loading}
            >
              <Text style={DC.cancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[DC.deleteBtn, loading && { opacity: 0.6 }]}
              onPress={onConfirm}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={DC.deleteText}>Delete</Text>
              )}
            </TouchableOpacity>
          </RNView>
        </RNView>
      </RNView>
    </Modal>
  );
};

// ─── Sort Picker ──────────────────────────────────────────────────────────────

interface SortPickerProps {
  visible: boolean;
  current: SortOption;
  onSelect: (v: SortOption) => void;
  onClose: () => void;
}

const SortPicker: React.FC<SortPickerProps> = ({
  visible,
  current,
  onSelect,
  onClose,
}) => {
  const C = useTokens();
  const SP = useMemo(() => createSP(C), [C]);
  return (
    <ResponsiveSheet visible={visible} onClose={onClose} maxWidth={360}>
      <RNView style={SP.titleRow}>
        <Text style={SP.title}>Sort by</Text>
        <CloseButton onPress={onClose} C={C} />
      </RNView>
      {SORT_OPTIONS.map((opt) => {
        const on = current === opt.value;
        return (
          <Pressable
            key={opt.value}
            style={(s: any) => [
              SP.option,
              (s.hovered || s.pressed) && SP.optionHover,
              on && SP.optionActive,
            ]}
            onPress={() => {
              onSelect(opt.value);
              onClose();
            }}
          >
            <FeatherIcon
              name={opt.icon}
              size={14}
              color={on ? C.accent : C.textMuted}
            />
            <Text style={[SP.optionText, on && SP.optionTextActive]}>
              {opt.label}
            </Text>
            {on && <FeatherIcon name="check" size={15} color={C.accent} />}
          </Pressable>
        );
      })}
    </ResponsiveSheet>
  );
};

// ─── Organisation Card ────────────────────────────────────────────────────────

interface OrgCardProps {
  org: Organization;
  viewMode: ViewMode;
  isRep: boolean;
  locked: boolean;
  onOpen: () => void;
  onCreateOrder: () => void;
}

const OrgCard: React.FC<OrgCardProps> = React.memo(
  ({ org, viewMode, isRep, locked, onOpen, onCreateOrder }) => {
    const C = useTokens();
    const OC = useMemo(() => createOC(C), [C]);
    const pricing = pricingLabel(org);
    const stateCount = org.states?.length ?? 0;
    const repCount = org.sales?.length ?? 0;

    const cardStyle = (base: any) => (s: any) => [
      base,
      !locked && s.hovered && OC.hovered,
      !locked && s.pressed && OC.pressed,
      locked && OC.locked,
    ];

    const a11y = {
      accessibilityRole: "button" as const,
      accessibilityState: { disabled: locked },
      accessibilityLabel: locked
        ? `${org.name}, no access`
        : `Open ${org.name}`,
    };

    if (viewMode === "list") {
      return (
        <Pressable
          style={cardStyle(OC.listRow)}
          onPress={onOpen}
          disabled={locked}
          {...a11y}
        >
          <Avatar org={org} size={44} C={C} />
          <RNView style={{ flex: 1, minWidth: 0, gap: 6 }}>
            <Text style={OC.listName} numberOfLines={1}>
              {org.name}
            </Text>
            <RNView style={OC.metaRow}>
              <Text style={OC.metaText} numberOfLines={1}>
                {codesSummary(org)}
              </Text>
              <RNView style={OC.dot} />
              <Text style={OC.metaText} numberOfLines={1}>
                {pricing.label}
              </Text>
              <RNView style={OC.dot} />
              <Text style={OC.metaText} numberOfLines={1}>
                {plural(stateCount, "state", "states")}
              </Text>
            </RNView>
          </RNView>
          <RNView style={OC.listPills}>
            <AccessPills isRep={isRep} locked={locked} C={C} />
          </RNView>
          {locked ? null : isRep ? (
            <TouchableOpacity
              style={OC.listOrderBtn}
              onPress={onCreateOrder}
              hitSlop={6}
              accessibilityLabel={`Create order for ${org.name}`}
            >
              <FeatherIcon name="shopping-cart" size={15} color={C.accent} />
            </TouchableOpacity>
          ) : (
            <FeatherIcon name="chevron-right" size={16} color={C.textMuted} />
          )}
        </Pressable>
      );
    }

    return (
      <Pressable
        style={cardStyle(OC.gridCard)}
        onPress={onOpen}
        disabled={locked}
        {...a11y}
      >
        <RNView style={OC.gridTop}>
          <Avatar org={org} size={44} C={C} />
          <RNView style={OC.cornerIcon}>
            <FeatherIcon
              name={locked ? "lock" : "more-horizontal"}
              size={14}
              color={C.textMuted}
            />
          </RNView>
        </RNView>

        <Text style={OC.gridName} numberOfLines={2}>
          {org.name}
        </Text>
        <Text style={OC.gridCode} numberOfLines={1}>
          {codesSummary(org)}
        </Text>

        <RNView style={OC.gridPills}>
          <Pill label={pricing.label} tone={pricing.tone} C={C} />
          <AccessPills isRep={isRep} locked={locked} compact C={C} />
        </RNView>

        <RNView style={OC.gridFooter}>
          <RNView style={OC.stat}>
            <FeatherIcon name="map-pin" size={12} color={C.textMuted} />
            <Text style={OC.statText}>
              {plural(stateCount, "state", "states")}
            </Text>
          </RNView>
          <RNView style={OC.stat}>
            <FeatherIcon name="users" size={12} color={C.textMuted} />
            <Text style={OC.statText}>{plural(repCount, "rep", "reps")}</Text>
          </RNView>
          <RNView style={{ flex: 1 }} />
          {isRep && !locked && (
            <TouchableOpacity
              style={OC.orderBtn}
              onPress={onCreateOrder}
              activeOpacity={0.8}
              accessibilityLabel={`Create order for ${org.name}`}
            >
              <FeatherIcon name="plus" size={12} color="#fff" />
              <Text style={OC.orderBtnText}>Order</Text>
            </TouchableOpacity>
          )}
        </RNView>
      </Pressable>
    );
  },
);

// Placeholder cards while the first page loads
const SkeletonCard = ({ viewMode, C }: { viewMode: ViewMode; C: Tokens }) => {
  const OC = useMemo(() => createOC(C), [C]);
  const block = (w: number | `${number}%`, h: number) => (
    <RNView
      style={{
        width: w,
        height: h,
        borderRadius: 6,
        backgroundColor: C.surfaceAlt,
      }}
    />
  );
  if (viewMode === "list") {
    return (
      <RNView style={OC.listRow}>
        {block(44, 44)}
        <RNView style={{ flex: 1, gap: 8 }}>
          {block("60%", 12)}
          {block("40%", 10)}
        </RNView>
      </RNView>
    );
  }
  return (
    <RNView style={[OC.gridCard, { gap: 10 }]}>
      {block(44, 44)}
      {block("85%", 12)}
      {block("50%", 10)}
      {block("60%", 18)}
    </RNView>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────

interface NavigationProp {
  navigate: (screen: string, params?: Record<string, unknown>) => void;
}

interface Props {
  navigation?: NavigationProp;
}

const CustomerOrganizationsListScreen: React.FC<Props> = ({ navigation }) => {
  const C = useTokens();
  const S = useMemo(() => createS(C), [C]);
  const dispatch = useDispatch<any>();
  const { isSmallDevice, isMobileDevice } = useContext(
    DeviceOrientationContext,
  );
  const { width } = useWindowDimensions();
  const isWide = width >= WIDE_BREAKPOINT;

  const {
    organizations: fetchedOrganizations,
    loading,
    loadingActionOrganization,
    page,
    pageSize,
    totalCount,
    totalPages,
    searchQuery,
    sortBy,
  } = useSelector((state: any) => state.portal.admin.organizations);

  // Signed-in user's role: "OWNER" | "ADMIN" | "MEMBER" — applies to every org
  const { currentRole } = useSelector((state: any) => state.configurations);
  const userRole = (currentRole ?? null) as UserRole | null;
  const isAdmin = !!userRole && ADMIN_ROLES.has(userRole);

  // ── Local UI state ────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [localSearch, setLocalSearch] = useState<string>(searchQuery ?? "");
  const [searchFocused, setSearchFocused] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sortPickerVisible, setSortPickerVisible] = useState<boolean>(false);
  const [contextMenuVisible, setContextMenuVisible] = useState<boolean>(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [deleteVisible, setDeleteVisible] = useState<boolean>(false);
  const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);
  const [addEditVisible, setAddEditVisible] = useState<boolean>(false);

  const isMobile = isSmallDevice || isMobileDevice;
  const columns =
    viewMode === "grid" ? (isMobile ? 2 : width >= 1200 ? 4 : 3) : 1;

  // ── Sales reps ────────────────────────────────────────────────────────────
  const { email: salesEmail, customers: salesCustomers } = useSelector(
    (state: any) => state.portal.admin.salesOrders,
  );

  const [salesOrderVisible, setSalesOrderVisible] = useState(false);
  const [salesOrderOrgId, setSalesOrderOrgId] = useState<string | null>(null);

  // The server decides which customers this user is a rep for
  useEffect(() => {
    dispatch(getSalesCustomers());
  }, [dispatch]);

  const repOrgIds = useMemo(
    () =>
      new Set<string>((salesCustomers ?? []).map((c: any) => String(c._id))),
    [salesCustomers],
  );

  // ── Access ────────────────────────────────────────────────────────────────
  // Open / manage: assigned sales rep, or an OWNER / ADMIN.
  // Create order: assigned sales rep only — owners and admins can't.
  const getAccess = useCallback(
    (org: Organization | null): OrgAccess => {
      const isRep = !!org && repOrgIds.has(String(org._id));
      const orgAdmin = !!org && isAdmin;
      return {
        isRep,
        isAdmin: orgAdmin,
        role: orgAdmin ? userRole : null,
        canAccess: isRep || orgAdmin,
      };
    },
    [repOrgIds, isAdmin, userRole],
  );

  const selectedAccess = useMemo(
    () => getAccess(selectedOrg),
    [getAccess, selectedOrg],
  );

  const accessibleOnPage = useMemo(
    () =>
      (fetchedOrganizations as Organization[]).filter(
        (o) => getAccess(o).canAccess,
      ).length,
    [fetchedOrganizations, getAccess],
  );

  const openSalesOrder = useCallback(
    (org: Organization | null) => {
      if (!org || !getAccess(org).isRep) return;
      setSalesOrderOrgId(org._id);
      setSalesOrderVisible(true);
    },
    [getAccess],
  );

  const closeSalesOrder = useCallback(() => {
    setSalesOrderVisible(false);
    setSalesOrderOrgId(null);
  }, []);

  // Same loaders the order summary uses for editing
  const loaders = useSalesOrderLoaders();

  const submitSalesOrder = useCallback(
    async (payload: SalesOrderPayload) => {
      // Throws on failure; the modal shows the server's message
      await dispatch(createSalesOrder(payload)).unwrap();
      closeSalesOrder();
    },
    [dispatch, closeSalesOrder],
  );

  // ── Debounced search ──────────────────────────────────────────────────────
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    },
    [],
  );

  const handleSearchChange = useCallback(
    (text: string) => {
      setLocalSearch(text);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        dispatch(setOrganizationSearchQuery(text));
        dispatch(setOrganizationPage(1));
      }, 400);
    },
    [dispatch],
  );

  const handleClearSearch = useCallback(() => {
    setLocalSearch("");
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    dispatch(setOrganizationSearchQuery(""));
    dispatch(setOrganizationPage(1));
  }, [dispatch]);

  // ── Fetch on search / sort / pagination changes ───────────────────────────
  useEffect(() => {
    dispatch(getOrganizations());
  }, [dispatch, page, pageSize, searchQuery, sortBy]);

  useEffect(() => {
    dispatch(listAllPortalStates());
  }, [dispatch]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        dispatch(getOrganizations()),
        dispatch(getSalesCustomers()),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleOpen = useCallback(
    (org: Organization) => {
      if (!getAccess(org).canAccess) return;
      setSelectedOrg(org);
      setContextMenuVisible(true);
    },
    [getAccess],
  );

  const handleEdit = useCallback(() => {
    if (!getAccess(selectedOrg).isAdmin) return;
    setAddEditVisible(true);
  }, [getAccess, selectedOrg]);

  const confirmDelete = useCallback(() => {
    if (!orgToDelete || !getAccess(orgToDelete).isAdmin) return;
    // dispatch(deleteOrganization(orgToDelete._id)).then(() => {
    //   dispatch(getOrganizations());
    // });
    setDeleteVisible(false);
    setOrgToDelete(null);
  }, [orgToDelete, getAccess]);

  const handleCancelDelete = useCallback(() => {
    setDeleteVisible(false);
    setOrgToDelete(null);
  }, []);

  const handleSortSelect = useCallback(
    (value: SortOption) => {
      dispatch(setOrganizationSortBy(value));
      dispatch(setOrganizationPage(1));
    },
    [dispatch],
  );

  const handlePrevPage = useCallback(() => {
    if (page > 1) dispatch(setOrganizationPage(page - 1));
  }, [dispatch, page]);

  const handleNextPage = useCallback(() => {
    if (page < totalPages) dispatch(setOrganizationPage(page + 1));
  }, [dispatch, page, totalPages]);

  const navigate = useCallback(
    (screen: string, params?: Record<string, unknown>) => {
      navigation?.navigate(screen, params);
    },
    [navigation],
  );

  const closeAddEdit = useCallback(() => {
    setAddEditVisible(false);
    setSelectedOrg(null);
  }, []);

  // Throws on failure so the modal stays open and shows the message
  const handleSave = useCallback(
    async (data: OrgSavePayload) => {
      if (!selectedOrg || !getAccess(selectedOrg).isAdmin) {
        throw new Error("You don't have permission to edit this organisation.");
      }
      await dispatch(
        updateOrganization({ id: selectedOrg._id, ...data }),
      ).unwrap();
      dispatch(getOrganizations());
      closeAddEdit();
    },
    [dispatch, selectedOrg, getAccess, closeAddEdit],
  );

  // ── Render item ───────────────────────────────────────────────────────────
  const renderItem = useCallback(
    ({ item }: { item: Organization }) => {
      const a = getAccess(item);
      return (
        <RNView style={{ width: `${100 / columns}%`, padding: 6 }}>
          <OrgCard
            org={item}
            viewMode={viewMode}
            isRep={a.isRep}
            locked={!a.canAccess}
            onOpen={() => handleOpen(item)}
            onCreateOrder={() => openSalesOrder(item)}
          />
        </RNView>
      );
    },
    [columns, viewMode, getAccess, handleOpen, openSalesOrder],
  );

  const keyExtractor = useCallback((item: Organization) => item._id, []);

  const currentSort = SORT_OPTIONS.find((o) => o.value === sortBy);
  const showSkeleton =
    loading && !refreshing && fetchedOrganizations.length === 0;

  const rangeStart = totalCount ? (page - 1) * (pageSize ?? 0) + 1 : 0;
  const rangeEnd = totalCount
    ? Math.min(page * (pageSize ?? 0), totalCount)
    : 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <View style={S.root}>
      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <RNView style={S.header}>
        <RNView style={[S.headerInner, isWide && S.headerInnerWide]}>
          <RNView style={S.titleBlock}>
            <RNView style={S.titleRow}>
              <Text style={S.title}>Customer Portal Orgs</Text>
              {isAdmin && (
                <Pill
                  label={roleLabel(userRole)}
                  icon="shield"
                  tone="accent"
                  C={C}
                />
              )}
            </RNView>
            <Text style={S.subtitle} numberOfLines={2}>
              {totalCount != null
                ? plural(totalCount, "organization", "organizations")
                : "Loading…"}
              {isAdmin
                ? " · you can manage all of them"
                : fetchedOrganizations.length > 0
                  ? ` · ${accessibleOnPage} of ${fetchedOrganizations.length} on this page assigned to you`
                  : ""}
            </Text>
          </RNView>

          <RNView style={[S.toolbar, isWide && S.toolbarWide]}>
            <RNView
              style={[
                S.searchWrap,
                isWide && S.searchWrapWide,
                searchFocused && S.searchWrapFocused,
              ]}
            >
              <FeatherIcon name="search" size={15} color={C.textMuted} />
              <TextInput
                style={[S.searchInput, webNoOutline]}
                value={localSearch}
                onChangeText={handleSearchChange}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Search by name or card code"
                placeholderTextColor={C.textMuted}
                returnKeyType="search"
                autoCorrect={false}
                autoCapitalize="none"
              />
              {localSearch.length > 0 && (
                <TouchableOpacity onPress={handleClearSearch} hitSlop={8}>
                  <FeatherIcon name="x-circle" size={15} color={C.textMuted} />
                </TouchableOpacity>
              )}
            </RNView>

            <RNView style={S.toolbarActions}>
              <Pressable
                style={(s: any) => [
                  S.sortBtn,
                  (s.hovered || s.pressed) && S.btnHover,
                ]}
                onPress={() => setSortPickerVisible(true)}
                accessibilityRole="button"
              >
                <FeatherIcon name="sliders" size={13} color={C.textMid} />
                <Text style={S.sortBtnText}>
                  {currentSort?.label ?? "Sort"}
                </Text>
                <FeatherIcon
                  name="chevron-down"
                  size={13}
                  color={C.textMuted}
                />
              </Pressable>

              <RNView style={S.viewToggle}>
                {(["grid", "list"] as ViewMode[]).map((mode) => {
                  const on = viewMode === mode;
                  return (
                    <TouchableOpacity
                      key={mode}
                      style={[S.viewToggleBtn, on && S.viewToggleBtnActive]}
                      onPress={() => setViewMode(mode)}
                      accessibilityLabel={`${mode} view`}
                      accessibilityState={{ selected: on }}
                    >
                      <FeatherIcon
                        name={mode}
                        size={14}
                        color={on ? C.accent : C.textMuted}
                      />
                    </TouchableOpacity>
                  );
                })}
              </RNView>
            </RNView>
          </RNView>
        </RNView>
      </RNView>

      {/* ══ BODY ════════════════════════════════════════════════════════════ */}
      {showSkeleton ? (
        <RNView style={[S.listContent, S.skeletonWrap]}>
          {Array.from({ length: columns * 3 }).map((_, i) => (
            <RNView key={i} style={{ width: `${100 / columns}%`, padding: 6 }}>
              <SkeletonCard viewMode={viewMode} C={C} />
            </RNView>
          ))}
        </RNView>
      ) : (
        <FlatList<Organization>
          data={fetchedOrganizations}
          keyExtractor={keyExtractor}
          key={`cols-${columns}`}
          numColumns={columns}
          contentContainerStyle={S.listContent}
          showsVerticalScrollIndicator={false}
          renderItem={renderItem}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={C.accent}
              colors={[C.accent]}
            />
          }
          ListEmptyComponent={
            <RNView style={S.emptyState}>
              <RNView style={S.emptyIconWrap}>
                <FeatherIcon
                  name={localSearch ? "search" : "briefcase"}
                  size={26}
                  color={C.accent}
                />
              </RNView>
              <Text style={S.emptyTitle}>
                {localSearch ? "No matches" : "No organisations yet"}
              </Text>
              <Text style={S.emptySubtitle}>
                {localSearch
                  ? `Nothing matches “${localSearch}”. Try a different name or card code.`
                  : "Organisations you're assigned to or administer will appear here."}
              </Text>
              {!!localSearch && (
                <TouchableOpacity
                  style={S.emptyBtn}
                  onPress={handleClearSearch}
                >
                  <Text style={S.emptyBtnText}>Clear search</Text>
                </TouchableOpacity>
              )}
            </RNView>
          }
          ListFooterComponent={
            totalPages > 1 ? (
              <RNView style={[S.pagination, isWide && S.paginationWide]}>
                <Text style={S.rangeText}>
                  {rangeStart}–{rangeEnd} of {totalCount}
                </Text>
                <RNView style={S.pageControls}>
                  {loading && (
                    <ActivityIndicator size="small" color={C.accent} />
                  )}
                  <TouchableOpacity
                    style={[S.pageBtn, page === 1 && S.pageBtnDisabled]}
                    onPress={handlePrevPage}
                    disabled={page === 1}
                    accessibilityLabel="Previous page"
                  >
                    <FeatherIcon name="chevron-left" size={16} color={C.text} />
                  </TouchableOpacity>
                  <Text style={S.pageInfo}>
                    <Text style={{ color: C.text, fontWeight: "800" }}>
                      {page}
                    </Text>{" "}
                    / {totalPages}
                  </Text>
                  <TouchableOpacity
                    style={[
                      S.pageBtn,
                      page === totalPages && S.pageBtnDisabled,
                    ]}
                    onPress={handleNextPage}
                    disabled={page === totalPages}
                    accessibilityLabel="Next page"
                  >
                    <FeatherIcon
                      name="chevron-right"
                      size={16}
                      color={C.text}
                    />
                  </TouchableOpacity>
                </RNView>
              </RNView>
            ) : null
          }
        />
      )}

      {/* ══ MODALS ══════════════════════════════════════════════════════════ */}
      <SortPicker
        visible={sortPickerVisible}
        current={sortBy}
        onSelect={handleSortSelect}
        onClose={() => setSortPickerVisible(false)}
      />

      <ContextMenu
        visible={contextMenuVisible && selectedAccess.canAccess}
        org={selectedOrg}
        access={selectedAccess}
        onClose={() => setContextMenuVisible(false)}
        onCreateOrder={() => openSalesOrder(selectedOrg)}
        onEdit={handleEdit}
        onViewUsers={() =>
          navigate("OrganizationMembers", {
            orgId: selectedOrg?._id,
            orgName: selectedOrg?.name,
          })
        }
        onViewOrders={() =>
          navigate("OrganizationOrdersScreen", {
            orgId: selectedOrg?._id,
            orgName: selectedOrg?.name,
          })
        }
        onViewGrowers={() =>
          navigate("AdminGrowers", {
            orgId: selectedOrg?._id,
            orgName: selectedOrg?.name,
          })
        }
        onViewProducts={() =>
          navigate("AdminProducts", {
            organization: selectedOrg as unknown as Record<string, unknown>,
          })
        }
        onViewDashboard={() =>
          navigate("AdminDashboard", {
            organization: selectedOrg as unknown as Record<string, unknown>,
          })
        }
        onAssignProducts={() =>
          navigate("CustomerAssignProducts", {
            organization: selectedOrg as unknown as Record<string, unknown>,
          })
        }
      />

      <DeleteConfirmModal
        visible={deleteVisible}
        orgName={orgToDelete?.name}
        loading={loadingActionOrganization}
        onConfirm={confirmDelete}
        onCancel={handleCancelDelete}
      />

      <AddEditOrganizationModal
        visible={addEditVisible && selectedAccess.isAdmin}
        organization={selectedOrg}
        onClose={closeAddEdit}
        onSave={handleSave}
      />

      {!!salesEmail && (
        <CreateSalesOrderModal
          visible={salesOrderVisible}
          onClose={closeSalesOrder}
          initialCustomerId={salesOrderOrgId}
          loadCustomers={loaders.loadCustomers}
          loadGrowers={loaders.loadGrowers}
          loadProducts={loaders.loadProducts}
          onSubmit={submitSalesOrder}
        />
      )}
    </View>
  );
};

export default CustomerOrganizationsListScreen;

// ─── Styles ───────────────────────────────────────────────────────────────────

const CONTENT_MAX_WIDTH = 1280;

const createS = (C: Tokens) =>
  StyleSheet.create({
    root: { flex: 1, backgroundColor: C.surfaceAlt },

    header: {
      backgroundColor: C.surface,
      borderBottomWidth: 1,
      borderBottomColor: C.border,
      paddingHorizontal: 16,
      paddingTop: 16,
      paddingBottom: 14,
    },
    headerInner: {
      width: "100%",
      maxWidth: CONTENT_MAX_WIDTH,
      alignSelf: "center",
      gap: 14,
    },
    headerInnerWide: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 24,
      paddingHorizontal: 6,
    },
    titleBlock: { flexShrink: 1, gap: 3 },
    titleRow: { flexDirection: "row", alignItems: "center", gap: 10 },
    title: { fontSize: 22, fontWeight: "800", color: C.text },
    subtitle: { fontSize: 13, color: C.textMuted },

    toolbar: { gap: 10 },
    toolbarWide: {
      flexDirection: "row",
      alignItems: "center",
      flexShrink: 0,
    },
    toolbarActions: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 8,
    },
    searchWrap: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      backgroundColor: C.surfaceAlt,
      borderRadius: 10,
      borderWidth: 1.5,
      borderColor: "transparent",
      paddingHorizontal: 12,
      height: 40,
    },
    searchWrapWide: { width: 320 },
    searchWrapFocused: { borderColor: C.accent, backgroundColor: C.surface },
    searchInput: { flex: 1, fontSize: 14, color: C.text, paddingVertical: 0 },

    sortBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      height: 40,
      paddingHorizontal: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: C.border,
      backgroundColor: C.surface,
    },
    btnHover: { backgroundColor: C.surfaceAlt },
    sortBtnText: { fontSize: 13, fontWeight: "600", color: C.text },
    viewToggle: {
      flexDirection: "row",
      backgroundColor: C.surfaceAlt,
      borderRadius: 10,
      padding: 3,
    },
    viewToggleBtn: {
      width: 34,
      height: 34,
      borderRadius: 8,
      justifyContent: "center",
      alignItems: "center",
    },
    viewToggleBtnActive: {
      backgroundColor: C.surface,
      shadowColor: "#000",
      shadowOpacity: 0.08,
      shadowRadius: 3,
      shadowOffset: { width: 0, height: 1 },
      elevation: 1,
    },

    listContent: {
      padding: 10,
      paddingBottom: 40,
      flexGrow: 1,
      width: "100%",
      maxWidth: CONTENT_MAX_WIDTH,
      alignSelf: "center",
    },
    skeletonWrap: { flexDirection: "row", flexWrap: "wrap" },

    emptyState: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      paddingTop: 80,
      gap: 8,
      paddingHorizontal: 32,
    },
    emptyIconWrap: {
      width: 60,
      height: 60,
      borderRadius: 18,
      backgroundColor: C.accent + "14",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: 6,
    },
    emptyTitle: { fontSize: 17, fontWeight: "800", color: C.text },
    emptySubtitle: {
      fontSize: 13,
      color: C.textMuted,
      textAlign: "center",
      lineHeight: 19,
      maxWidth: 360,
    },
    emptyBtn: {
      marginTop: 8,
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderRadius: 20,
      backgroundColor: C.accent + "14",
    },
    emptyBtnText: { fontSize: 13, fontWeight: "700", color: C.accent },

    pagination: {
      alignItems: "center",
      gap: 10,
      paddingTop: 18,
      paddingBottom: 8,
      paddingHorizontal: 6,
    },
    paginationWide: {
      flexDirection: "row",
      justifyContent: "space-between",
    },
    rangeText: { fontSize: 12, color: C.textMuted, fontWeight: "600" },
    pageControls: { flexDirection: "row", alignItems: "center", gap: 12 },
    pageBtn: {
      width: 36,
      height: 36,
      borderRadius: 10,
      backgroundColor: C.surface,
      borderWidth: 1,
      borderColor: C.border,
      justifyContent: "center",
      alignItems: "center",
    },
    pageBtnDisabled: { opacity: 0.35 },
    pageInfo: {
      fontSize: 13,
      fontWeight: "600",
      color: C.textMuted,
      minWidth: 44,
      textAlign: "center",
    },
  });

const createRS = (C: Tokens) =>
  StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: C.overlay },
    backdropCenter: {
      justifyContent: "center",
      alignItems: "center",
      padding: 24,
    },
    backdropBottom: { justifyContent: "flex-end" },
    panel: { backgroundColor: C.surface },
    panelDialog: {
      width: "100%",
      borderRadius: 16,
      padding: 20,
      borderWidth: 1,
      borderColor: C.border,
      shadowColor: "#000",
      shadowOpacity: 0.18,
      shadowRadius: 24,
      shadowOffset: { width: 0, height: 12 },
      elevation: 8,
    },
    panelSheet: {
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      paddingHorizontal: 20,
      paddingTop: 12,
      paddingBottom: 28,
    },
    handle: {
      width: 36,
      height: 4,
      borderRadius: 2,
      backgroundColor: C.border,
      alignSelf: "center",
      marginBottom: 14,
    },
  });

const createDC = (C: Tokens) =>
  StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: C.overlay,
      justifyContent: "center",
      alignItems: "center",
      padding: 24,
    },
    card: {
      width: "100%",
      maxWidth: 380,
      backgroundColor: C.surface,
      borderRadius: 16,
      padding: 24,
      gap: 10,
      borderWidth: 1,
      borderColor: C.border,
    },
    iconWrap: {
      width: 48,
      height: 48,
      borderRadius: 14,
      backgroundColor: C.dangerBg,
      justifyContent: "center",
      alignItems: "center",
      marginBottom: 4,
    },
    title: { fontSize: 17, fontWeight: "800", color: C.text },
    body: { fontSize: 13, color: C.textMid, lineHeight: 20 },
    actions: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: 10,
      marginTop: 10,
    },
    cancelBtn: {
      height: 40,
      paddingHorizontal: 16,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: C.border,
      justifyContent: "center",
      alignItems: "center",
    },
    cancelText: { fontSize: 14, fontWeight: "700", color: C.textMid },
    deleteBtn: {
      height: 40,
      minWidth: 96,
      paddingHorizontal: 16,
      borderRadius: 10,
      backgroundColor: C.danger,
      justifyContent: "center",
      alignItems: "center",
    },
    deleteText: { fontSize: 14, fontWeight: "800", color: "#fff" },
  });

const createCM = (C: Tokens) =>
  StyleSheet.create({
    header: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 12,
      marginBottom: 16,
    },
    headerTitle: { fontSize: 16, fontWeight: "800", color: C.text },
    headerSub: { fontSize: 12, color: C.textMuted },
    headerPills: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
      marginTop: 2,
    },
    primaryBtn: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      height: 44,
      borderRadius: 10,
      backgroundColor: C.accent,
      marginBottom: 14,
    },
    primaryBtnText: { fontSize: 14, fontWeight: "800", color: "#fff" },
    group: {
      borderWidth: 1,
      borderColor: C.border,
      borderRadius: 12,
      overflow: "hidden",
    },
    groupLabel: {
      fontSize: 11,
      fontWeight: "800",
      color: C.textMuted,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      marginTop: 16,
      marginBottom: 8,
    },
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      paddingHorizontal: 12,
      paddingVertical: 10,
      backgroundColor: C.surface,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: C.border,
    },
    rowHover: { backgroundColor: C.surfaceAlt },
    rowIcon: {
      width: 34,
      height: 34,
      borderRadius: 9,
      backgroundColor: C.accent + "12",
      justifyContent: "center",
      alignItems: "center",
    },
    rowLabel: { fontSize: 14, fontWeight: "700", color: C.text },
    rowDesc: { fontSize: 12, color: C.textMuted, marginTop: 1 },
  });

const createSP = (C: Tokens) =>
  StyleSheet.create({
    titleRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8,
    },
    title: { fontSize: 16, fontWeight: "800", color: C.text },
    option: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      paddingVertical: 12,
      paddingHorizontal: 10,
      borderRadius: 10,
    },
    optionHover: { backgroundColor: C.surfaceAlt },
    optionActive: { backgroundColor: C.accent + "10" },
    optionText: { flex: 1, fontSize: 14, fontWeight: "600", color: C.textMid },
    optionTextActive: { color: C.accent, fontWeight: "700" },
  });

const createOC = (C: Tokens) =>
  StyleSheet.create({
    hovered: {
      borderColor: C.accent + "66",
      shadowOpacity: 0.1,
      shadowRadius: 14,
      transform: [{ translateY: -1 }],
    },
    pressed: { opacity: 0.85 },
    locked: { opacity: 0.55 },

    // List
    listRow: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: C.surface,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: C.borderLight,
      paddingVertical: 12,
      paddingHorizontal: 14,
      gap: 14,
      shadowColor: C.navy,
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.04,
      shadowRadius: 6,
      elevation: 1,
    },
    listName: { fontSize: 15, fontWeight: "700", color: C.text },
    metaRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      flexWrap: "wrap",
    },
    metaText: { fontSize: 12, color: C.textMuted, fontWeight: "600" },
    dot: {
      width: 3,
      height: 3,
      borderRadius: 2,
      backgroundColor: C.textMuted,
      opacity: 0.6,
    },
    listPills: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "flex-end",
      gap: 6,
      maxWidth: "40%",
    },
    listOrderBtn: {
      width: 36,
      height: 36,
      borderRadius: 10,
      backgroundColor: C.accent + "14",
      justifyContent: "center",
      alignItems: "center",
    },

    // Grid
    gridCard: {
      flex: 1,
      backgroundColor: C.surface,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: C.borderLight,
      padding: 16,
      minHeight: 196,
      shadowColor: C.navy,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.05,
      shadowRadius: 8,
      elevation: 1,
    },
    gridTop: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 12,
    },
    cornerIcon: {
      width: 28,
      height: 28,
      borderRadius: 8,
      backgroundColor: C.surfaceAlt,
      justifyContent: "center",
      alignItems: "center",
    },
    gridName: {
      fontSize: 15,
      fontWeight: "800",
      color: C.text,
      lineHeight: 20,
    },
    gridCode: {
      fontSize: 12,
      color: C.textMuted,
      fontWeight: "600",
      marginTop: 3,
    },
    gridPills: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 5,
      marginTop: 12,
    },
    gridFooter: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      marginTop: "auto",
      paddingTop: 12,
      borderTopWidth: 1,
      borderTopColor: C.borderLight,
    },
    stat: { flexDirection: "row", alignItems: "center", gap: 4 },
    statText: { fontSize: 12, fontWeight: "600", color: C.textMid },
    orderBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      height: 28,
      paddingHorizontal: 10,
      borderRadius: 14,
      backgroundColor: C.accent,
    },
    orderBtnText: { fontSize: 12, fontWeight: "800", color: "#fff" },
  });
